import * as OfferService from 'api/offer'
import { getSnapshotDetailsForTourV2Offer } from 'api/tourV2'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { CreateTripItemRequest } from 'tripPlanner/api/tripItem/types'
import {
  convertFlightItemToFlightTripItem,
  convertOrderBedbankItemToHotelTripItem,
  convertOrderExperienceItemToExperienceTripItem,
  convertOrderItemToCruiseV2TripItem,
  convertOrderItemToTourOrCruiseTripItem,
  convertStandardOrderItemToAccommodationTripItem,
  createTourV2Payload,
  getFlightsFromFlightOrder,
} from 'tripPlanner/utils/itemConversions/common'

interface ConvertTripItemsFromOrderItemsParams {
  order: App.Order
  offers: Array<App.AnyOffer>
  currentRegionCode: string
  currentCurrency: string
}

export const convertTripItemsFromOrderItems = async ({
  order,
  offers,
  currentRegionCode,
  currentCurrency,
}: ConvertTripItemsFromOrderItemsParams): Promise<CreateTripItemRequest[]> => {
  const createTripItemRequests: CreateTripItemRequest[] = []

  // Experiences
  if (order.experienceItems.length > 0) {
    // Find all the unique experience ids making sure we don't duplicate.
    const processedExperienceIds: string[] = []

    for (const experienceOrderItem of order.experienceItems) {
      if (
        !processedExperienceIds.includes(experienceOrderItem.experienceId) &&
        experienceOrderItem.status !== 'cancelled'
      ) {
        processedExperienceIds.push(experienceOrderItem.experienceId)

        const expItem = await convertOrderExperienceItemToExperienceTripItem({
          orderItem: experienceOrderItem,
          orderId: order.id,
          currentCurrency,
          currentRegionCode,
          orderItemIds: order.experienceItems
            .filter(
              (experienceItem) =>
                experienceItem.experienceId ===
                experienceOrderItem.experienceId,
            )
            .map((experienceItem) => experienceItem.id),
        })

        createTripItemRequests.push(expItem)
      }
    }
  }

  // Flights
  for (const flightOrderItem of order.flightItems) {
    if (flightOrderItem.status !== 'cancelled') {
      const bookedFlights = await getFlightsFromFlightOrder(flightOrderItem)
      for (const flight of bookedFlights) {
        const bookedFlightTripItem = convertFlightItemToFlightTripItem(
          flight,
          flightOrderItem,
        )
        createTripItemRequests.push(bookedFlightTripItem)
      }
    }
  }

  // LE Offers Accommodation and Tours/Cruises
  if (order.items.length > 0) {
    // Find all the unique offer ids making sure we don't duplicate.
    const processedOfferIds: string[] = []
    for (const orderItem of order.items) {
      if (
        !processedOfferIds.includes(orderItem.offerId) &&
        orderItem.status !== 'cancelled'
      ) {
        processedOfferIds.push(orderItem.offerId)
        const offer = offers.find(
          (o): o is App.Offer => o?.id === orderItem.offerId,
        )

        if (offer && offer.type === OFFER_TYPE_TOUR) {
          const tourOrCruiseTripItem = convertOrderItemToTourOrCruiseTripItem(
            order,
            orderItem,
            offer,
            currentRegionCode,
            order.items
              .filter((tourItem) => tourItem.offerId === orderItem.offerId)
              .map((tourItem) => tourItem.id),
          )

          createTripItemRequests.push(tourOrCruiseTripItem)
        } else {
          const accommodationTripItem =
            convertStandardOrderItemToAccommodationTripItem(
              order,
              orderItem,
              offer,
              currentRegionCode,
              order.items
                .filter(
                  (accommodationItem) =>
                    accommodationItem.offerId === orderItem.offerId,
                )
                .map((accommodationItem) => accommodationItem.id),
            )

          createTripItemRequests.push(accommodationTripItem)
        }
      }
    }
  }

  // CruisesV2
  if (order.cruiseItems.length > 0) {
    const processedOfferIds: string[] = []
    for (const orderItem of order.cruiseItems) {
      if (
        !processedOfferIds.includes(orderItem.cruiseOfferId) &&
        orderItem.status !== 'cancelled'
      ) {
        const offer = offers.find(
          (o): o is App.CruiseOffer => o.id === orderItem.cruiseOfferId,
        )

        if (offer) {
          const cruiseTripItem = convertOrderItemToCruiseV2TripItem(
            order,
            orderItem,
            offer,
            currentRegionCode,
            order.items
              .filter((cruiseItem) => cruiseItem.id === orderItem.cruiseOfferId)
              .map((cruiseItem) => cruiseItem.id),
          )

          createTripItemRequests.push(cruiseTripItem)
        }

        processedOfferIds.push(orderItem.cruiseOfferId)
      }
    }
  }

  // LE TourV2
  if (order.tourItems.length > 0) {
    // Find all the unique tour ids making sure we don't duplicate.
    const processedTourIds: string[] = []
    for (const orderItem of order.tourItems) {
      if (
        !processedTourIds.includes(orderItem.tourId) &&
        orderItem.status !== 'cancelled'
      ) {
        // ToDo: Is there a better way of fetching TourV2 Offer?
        const fetchTourV2OfferById = OfferService.getOfferById(
          orderItem.tourId,
          {
            region: currentRegionCode,
          },
        )
        const fetchTourV2SnapshotById = getSnapshotDetailsForTourV2Offer({
          reservationId: orderItem.reservationId,
        })

        await Promise.all([fetchTourV2OfferById, fetchTourV2SnapshotById]).then(
          ([offer, snapshot]) => {
            if (offer && isTourV2Offer(offer)) {
              const tourV2Item = createTourV2Payload(
                order,
                orderItem,
                offer,
                currentRegionCode,
                order.tourItems
                  .filter((tourItem) => tourItem.tourId === orderItem.tourId)
                  .map((tourItem) => tourItem.itemId),
                snapshot,
              )

              processedTourIds.push(orderItem.tourId)
              createTripItemRequests.push(tourV2Item)
            }
          },
        )
      }
    }
  }

  // Bedbank Accommodation
  for (const bedbankOrderItem of order.bedbankItems) {
    if (bedbankOrderItem.status !== 'cancelled') {
      const convertedBedbankItemToHotelTripItem =
        convertOrderBedbankItemToHotelTripItem(bedbankOrderItem)
      createTripItemRequests.push(convertedBedbankItemToHotelTripItem)
    }
  }

  return createTripItemRequests
}
